import styles from "../../../styles/Components/trading-assistant/tradeAssistantCards.module.sass";
import TradeButton from "../../../shared/trade-form/TradeButton";
import HideContentIcon from "../../mini-app/shared/HideContentIcon";
import { Popover } from "antd";
import { currencyFormatter } from "../../../utility/helper";
import { FaCheck } from "react-icons/fa";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import AIAssistantServiceActivationModal from "../AIAssistantServiceActivationModal";
import {
  IoCopyOutline,
  IoInformationCircleOutline,
} from "react-icons/io5";
import SignalMoreInfo from "./SignalMoreInfo";
import useSuggestedSignal from "../../../hooks/useSuggestedSignal";
import LiveAIButton from "../../new-dashboard/LiveAIButton";
import { Typography } from "antd";
import PositionSizeLeverage from "../../../shared/components/PositionSizeLeverage";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { openGuideChat } from "../../../shared/GuideChat";
import capitalize from "antd/es/_util/capitalize";
import { liveSuggestionFor, normalizeLiveTimeframe } from "../../../redux/live-suggestion/liveSuggestionSlice";
import { stockDisplayName, stockTickerCode } from "../../../shared/stockBoards";

const { Paragraph } = Typography;

const GUIDE_ASSISTANT =
  "https://cryptocrispy.com.au/blog/2026-10-05-live-ai-versus-ai-assistant.html";
const GUIDE_CARD =
  "https://cryptocrispy.com.au/blog/2026-10-05-how-to-read-a-signal-card.html";

function GuideLink({ href, children }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
}

const MARK_COLORS = [
  "#1f4e79", "#0e6b4f", "#8c2f39", "#3d4c8a", "#0f6e8c",
  "#6b4c1e", "#245c3a", "#7a3e6d", "#1d4e4a", "#5c3d1e",
  "#2c4a7c", "#8a3d2f", "#1e5a4a", "#4a3d7a", "#3e5c22",
];

function markColor(symbol) {
  const text = String(symbol || "");
  let hash = 0;
  for (let index = 0; index < text.length; index += 1) {
    hash = (hash * 33 + text.charCodeAt(index)) >>> 0;
  }
  return MARK_COLORS[hash % MARK_COLORS.length];
}

function markLetters(symbol) {
  const stem = String(symbol || "").split("/")[0].replace(/=X$/i, "");
  const named = stockDisplayName(stem);
  if (named !== stem) {
    const words = named.split(/\s+/).filter(Boolean);
    if (words.length > 1) {
      return words.slice(0, 2).map((word) => word[0]).join("").toUpperCase();
    }
    return named.slice(0, 2).toUpperCase();
  }
  const core = stem.split(".")[0].replace(/-/g, "");
  const letters = core.replace(/[^A-Za-z0-9\u0600-\u06FF]/g, "");
  if (!letters) return "?";
  if (/[\u0600-\u06FF]/.test(letters)) return letters.slice(0, 2);
  return letters.slice(0, 2).toUpperCase();
}

function SymbolMark({ symbol }) {
  const [failed, setFailed] = useState(false);
  const stem = String(symbol || "").replace("/USDT", "");
  const named = stockDisplayName(stem) !== stem.replace(/=X$/i, "");
  if (named || failed || !stem) {
    return (
      <span className={styles.symbolMark} style={{ background: markColor(stem) }}>
        {markLetters(stem)}
      </span>
    );
  }
  return (
    <LazyLoadImage
      src={`https://cryptocrispypro.s3.ap-southeast-2.amazonaws.com/symbol_icons/${encodeURIComponent(stem)}.png`}
      alt={symbol}
      onError={() => setFailed(true)}
      width="35px"
      height="35px"
      effect="blur"
      style={{ borderRadius: "100%" }}
    />
  );
}

function TradeAssistantCard({ data }) {
  const {
    tradingAssistant: { aiMarketType, selectedTimeFrame },
    profile: { profileData },
    liveSuggestion: { suggestions, liveTimeframe, coinStatus },
    otherCoinsAnalyze: { analyze },
  } = useSelector((state) => state);

  const isAnalyzed = analyze?.find(
    (signal) =>
      signal?.SYMBOL_NAME?.toLowerCase()?.split("/usdt")?.[0] ===
      data?.SYMBOL_NAME?.toLowerCase()?.split("/usdt")?.[0],
  );

  const [showServiceActivationModal, setShowServiceActivationModal] =
    useState(false);

  const coin = data?.SYMBOL_NAME;
  const priceColor = data?.priceColor;
  const exchangeId = data?.EXCHANGE_ID || "binance";

  const {
    signal,
    signalColor,
    aiData,
    stopLoss: hookStopLoss,
    takeProfit: hookTakeProfit,
    positionSize: hookPositionSize,
    signalDate,
    aiLeverage,
  } = useSuggestedSignal({ coin, oppositeSignal: "ai" });

  // Use timeframe-based values if available, otherwise fallback to hook values
  const timeFrameData = data?.MT_SLTP?.[selectedTimeFrame];
  const stopLoss =
    timeFrameData?.STOP_LOSS_PERCENT || data?.STOP_LOSS_PERCENT || hookStopLoss;
  const takeProfit =
    timeFrameData?.TAKE_PROFIT_PERCENT ||
    data?.TAKE_PROFIT_PERCENT ||
    hookTakeProfit;
  const positionSize = hookPositionSize; // Keep using hook value for position size as it's calculated differently

  const isServiceActive = profileData?.myais_active === "True";

  const stockBoard = Boolean(data?.stockBoard);
  const quoteFrame = stockBoard ? "1d" : liveTimeframe;
  const liveData = liveSuggestionFor(suggestions, data?.SYMBOL_NAME, quoteFrame);
  const liveSide = stockBoard && (liveData?.miss || liveData?.price == null)
    ? ""
    : String(liveData?.prediction || "").split("/")[0];
  const liveTf = stockBoard ? "1d" : normalizeLiveTimeframe(liveTimeframe);
  const reading = stockBoard && coinStatus?.[data?.SYMBOL_NAME] === "loading" && !liveSide;
  const cardFrame = selectedTimeFrame || "1h";
  const assistantSide = String(data?.TRADE_TYPE || "").split("/")[0];
  const sidesDiffer =
    aiMarketType === "crypto" &&
    (liveSide === "Buy" || liveSide === "Sell") &&
    assistantSide &&
    assistantSide !== liveSide;

  const prediction =
    aiMarketType === "crypto" &&
    liveData?.prediction &&
    liveData?.prediction !== "Hold"
      ? liveData?.prediction
      : data?.TRADE_TYPE?.split("/")?.[0];

  const pivotOn = stockBoard
    ? liveSide === "Buy" || liveSide === "Sell"
    : aiMarketType === "crypto" && (liveSide === "Buy" || liveSide === "Sell");
  const trainingRequested = Boolean(liveData?.trainRequested);
  const coinStem = String(data?.SYMBOL_NAME || "").split("/")[0];
  const pivotState = coinStatus?.[data?.SYMBOL_NAME] || coinStatus?.[coinStem];
  const pivotPending = !liveData && pivotState !== "error";
  const pivotLabel = stockBoard
    ? liveSide
      ? `${liveSide} on 1d`
      : trainingRequested
        ? "Training was requested"
        : reading
          ? "Reading…"
          : coinStatus?.[data?.SYMBOL_NAME] === "error"
            ? "That daily call did not load."
            : "No pivot yet"
    : pivotOn
      ? `${liveSide} on ${liveTf}`
      : trainingRequested
        ? "Training was requested"
        : "No pivot";
  const stockSideClass = liveSide === "Buy"
    ? styles.sideBuy
    : liveSide === "Sell"
      ? styles.sideSell
      : styles.sideHold;
  const categorySignal = aiData?.signal || signal;
  const categoryColor = aiData?.signalColor || signalColor;
  const categoryName = data?.CATEGORY_INFO?.category || "";
  const availableExchanges = data?.AVAILABLE_EXCHANGES;

  return (
    <div
      className={`${styles.card} ${!!isAnalyzed ? styles.analyzedSignal : ""}`}
    >
      {!!isAnalyzed && <span className={styles.analyzedBadge}>Analyzed</span>}

      <div className={styles.cardHeader}>
        <div className={styles.leftHeader}>
          {stockBoard ? (
            <SymbolMark symbol={data?.SYMBOL_NAME} />
          ) : (
            <LazyLoadImage
              src={`https://cryptocrispypro.s3.ap-southeast-2.amazonaws.com/symbol_icons/${data?.SYMBOL_NAME?.replace("/USDT", "")}.png`}
              alt={data?.SYMBOL_NAME}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/placeholders/asset-logo.svg";
              }}
              width="35px"
              height="35px"
              effect="blur"
              style={{ borderRadius: "100%" }}
            />
          )}
          <div className={styles.title}>
            <span className={styles.assetName}>
              {stockBoard ? stockDisplayName(data?.SYMBOL_NAME) : data?.SYMBOL_NAME?.split("/")?.[0]?.replace(/=X$/i, "")}
              {!stockBoard && aiMarketType === "crypto" ? "/USDT" : ""}
            </span>
            <span className={styles.time}>
              {stockBoard && stockTickerCode(data?.SYMBOL_NAME)
                ? stockTickerCode(data?.SYMBOL_NAME)
                : signalDate}
            </span>
            <button
              type="button"
              className={styles.analyseCoin}
              onClick={() =>
                openGuideChat({ deep: "markets", symbol: data?.SYMBOL_NAME })
              }
            >
              Analyse
            </button>
          </div>
        </div>

        <TradeButton
          disabled={aiMarketType !== "crypto"}
          btnClassName={`${
            aiMarketType !== "crypto" || isServiceActive
              ? (stockBoard ? liveSide : prediction) === "Buy"
                ? styles.buy
                : (stockBoard ? liveSide : prediction) === "Sell"
                  ? styles.sell
                  : styles.hold
              : styles.hideContent
          } ${styles.tradeButton}${aiMarketType !== "crypto" ? ` ${styles.tradeInactive}` : ""}`}
          data={{ coin: data?.SYMBOL_NAME }}
          style={{ textAlign: "center", marginLeft: "auto" }}
          service="aiAssistant"
          onClick={() => {
            if (aiMarketType !== "crypto") return;
            if (!isServiceActive) {
              return setShowServiceActivationModal(true);
            }
          }}
        >
          {aiMarketType !== "crypto" ? (
            "Trade"
          ) : !isServiceActive ? (
            <HideContentIcon style={{ padding: ".5rem" }} />
          ) : (
            <>
              {pivotOn ? `Trade ${liveSide}` : "Trade"}
            </>
          )}
        </TradeButton>
      </div>

      <div className={styles.infoSection}>
        <div className={`${styles.info} ${styles.latestSignal}`}>
          <span>
            {stockBoard ? "Signal" : `AI Assistant${categoryName ? ` · ${categoryName}` : ""}`}:
            <Popover
              content={
                <div style={{ maxWidth: 260 }}>
                  <p>
                    This is the category call. The open mode uses one family,
                    such as Technical Indicators, On-chain, Social, or AI. The
                    info icon names that category. Hold means that category did
                    not open a Buy or Sell.
                  </p>
                  <GuideLink href={GUIDE_ASSISTANT}>Pivot versus the assistant</GuideLink>
                </div>
              }
              placement="bottom"
            >
              <IoInformationCircleOutline style={{ marginLeft: 4 }} />
            </Popover>
          </span>
          {stockBoard ? (
            <span className={stockSideClass}>{liveSide || (reading ? "Reading…" : pivotLabel)}</span>
          ) : isServiceActive ? (
            <div className={styles.signalValue}>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: ".2rem",
                }}
              >
                <Popover
                  content={<SignalMoreInfo data={data} />}
                  placement="bottom"
                  className={styles.categoryTooltip}
                >
                  {/*<FaCircleInfo />*/}
                  <IoInformationCircleOutline />
                </Popover>
                <span style={{ color: categoryColor, fontWeight: "bold" }}>
                  {categorySignal}
                </span>
              </span>
              {/*<span className={styles.time}>{signalDate}</span>*/}
            </div>
          ) : (
            <HideContentIcon
              clickHandler={(e) => {
                e.stopPropagation();
                setShowServiceActivationModal(true);
              }}
            />
          )}
        </div>

        <div className={styles.info}>
          <span>
            Pivot:
            <Popover
              content={
                <div style={{ maxWidth: 260 }}>
                  <p>
                    This looks for a turn on the timeframe you pick. It is the
                    pivot check, not the category call. 15m and 1h are shorter
                    turns. 1d is the daily turn.
                  </p>
                  <GuideLink href={`${GUIDE_CARD}#no-live-call`}>
                    How to read this row
                  </GuideLink>
                </div>
              }
              placement="bottom"
            >
              <IoInformationCircleOutline style={{ marginLeft: 4 }} />
            </Popover>
          </span>
          <span className={stockBoard ? stockSideClass : (pivotOn ? styles.pivotOn : styles.pivotOff)}>
            {pivotLabel}
          </span>
        </div>
        {!stockBoard && !pivotOn && !reading && !pivotPending ? (
          <button
            type="button"
            className={styles.analyseCoin}
            onClick={() =>
              openGuideChat(`What about ${coinStem}? There is no pivot.`)
            }
          >
            Ask about this coin
          </button>
        ) : null}
        {sidesDiffer ? (
          <p className={styles.disagree}>
            The pivot disagrees with the category call. For a longer hold, follow the assistant.
          </p>
        ) : null}

        {stockBoard ? (
          <>
            <div className={styles.info}>
              <span>Current Price:</span>
              <span>{liveData?.price ? currencyFormatter(liveData.price) : "—"}</span>
            </div>
            <div className={styles.info}>
              <span>P/E:</span>
              <span>
                {liveData?.pe
                  ? Number(liveData.pe).toFixed(1)
                  : liveData?.valuation || "—"}
              </span>
            </div>
            <div className={styles.info}>
              <span>Technical:</span>
              <span>
                {liveData?.rsi
                  ? `RSI ${liveData.rsi}${liveData?.trend ? ` · ${liveData.trend}` : ""}`
                  : "—"}
              </span>
            </div>
            <div className={styles.info}>
              <span>Stop (1d):</span>
              <span>{liveData?.stop_percent != null ? `${liveData.stop_percent}%` : "—"}</span>
            </div>
            <div className={styles.info}>
              <span>Target (1d):</span>
              <span>{liveData?.target_percent != null ? `${liveData.target_percent}%` : "—"}</span>
            </div>
          </>
        ) : (
        <>
        <div className={styles.info}>
          <span>Current Price:</span>
          <span style={priceColor ? { color: `${priceColor}` } : {}}>
            ${currencyFormatter(data?.CURRENT_PRICE)}
          </span>
        </div>

        <div className={styles.info}>
          <span>Stop ({cardFrame}):</span>
          {isServiceActive ? (
            <Paragraph
              copyable={{
                text: `${Number(stopLoss)?.toPrecision(2)}`,
                icon: [
                  <IoCopyOutline color="#333" key="copy-icon" />,
                  <FaCheck color="green" key="copied-icon" />,
                ],
                tooltips: ["Copy", "Copied!"],
              }}
              className={styles.copyableText}
            >
              {Number(stopLoss)?.toPrecision(2)}%
            </Paragraph>
          ) : (
            <HideContentIcon />
          )}
        </div>

        <div className={styles.info}>
          <span>Target ({cardFrame}):</span>
          {isServiceActive ? (
            <Paragraph
              copyable={{
                text: `${Number(takeProfit)?.toPrecision(2)}`,
                icon: [
                  <IoCopyOutline color="#333" key="copy-icon" />,
                  <FaCheck color="green" key="copied-icon" />,
                ],
                tooltips: ["Copy", "Copied!"],
              }}
              className={styles.copyableText}
            >
              {Number(takeProfit)?.toPrecision(2)}%
            </Paragraph>
          ) : (
            <HideContentIcon />
          )}
        </div>

        <div className={styles.info}>
          <span>Position Size:</span>
          {isServiceActive ? (
            <Paragraph
              copyable={{
                text: `${Number(positionSize)?.toFixed(2)}`,
                icon: [
                  <IoCopyOutline color="#333" key="copy-icon" />,
                  <FaCheck color="green" key="copied-icon" />,
                ],
                tooltips: ["Copy", "Copied!"],
              }}
              className={styles.copyableText}
            >
              {Number(positionSize)?.toFixed(2)}%
            </Paragraph>
          ) : (
            <HideContentIcon />
          )}
        </div>
        <div className={styles.info}>
          <span>Leverage:</span>
          {isServiceActive ? (
            <PositionSizeLeverage
              stopLoss={Number(Number(stopLoss)?.toFixed(2))}
              suggestedLeverage={aiLeverage}
            />
          ) : (
            <HideContentIcon />
          )}
        </div>
        </>
        )}
        {aiMarketType === "crypto" && (
          <div className={styles.info}>
            <span>Exchange:</span>
            {isServiceActive ? (
              <span>{capitalize(exchangeId)}</span>
            ) : (
              <HideContentIcon />
            )}
          </div>
        )}
      </div>

      {aiMarketType === "crypto" && (
        <LiveAIButton
          className={styles.runAiButton}
          coin={coin}
          exchange={availableExchanges?.[0] || ""}
          title="Run AI Backtest"
          iconColor="white"
        />
      )}

      <AIAssistantServiceActivationModal
        showActivationModal={showServiceActivationModal}
        setShowActivationModal={setShowServiceActivationModal}
      />
    </div>
  );
}

export default TradeAssistantCard;
